import { useEffect, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section } from "@/components/ui-brand";

const errMsg = (e) => e.response?.data?.detail || "No se pudo verificar el código";

const RecoveryCodes = ({ codes, onContinue }) => {
  const [saved, setSaved] = useState(false);
  const download = () => {
    const url = URL.createObjectURL(new Blob([codes.join("\n") + "\n"], { type: "text/plain" }));
    Object.assign(document.createElement("a"), { href: url, download: "codigos-recuperacion-ccf.txt" }).click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="mfa-recovery-codes">
      <h1 className="h-section text-2xl mb-1">Guardá tus códigos de recuperación</h1>
      <p className="text-sm text-[var(--brand-text-muted)] mb-4">Cada código sirve una sola vez si perdés acceso a tu app. No los vamos a volver a mostrar.</p>
      <ul className="grid grid-cols-2 gap-2 font-mono text-sm mb-4">{codes.map((c) => <li key={c} className="rounded-lg bg-[var(--brand-surface)] px-3 py-2 text-center">{c}</li>)}</ul>
      <div className="flex gap-2 mb-4">
        <button className="btn-outline text-sm" onClick={() => navigator.clipboard.writeText(codes.join("\n")).then(() => toast.success("Copiados"))} data-testid="mfa-codes-copy">Copiar</button>
        <button className="btn-outline text-sm" onClick={download} data-testid="mfa-codes-download">Descargar .txt</button>
      </div>
      <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="mfa-codes-saved" /> Los guardé en un lugar seguro</label>
      <button className="btn-primary w-full" disabled={!saved} onClick={onContinue} data-testid="mfa-codes-continue">Continuar</button>
    </div>
  );
};

const CodeForm = ({ onSubmit, recovery, setRecovery, busy }) => {
  const [code, setCode] = useState("");
  return (
    <form noValidate onSubmit={(e) => { e.preventDefault(); onSubmit(code); }} className="space-y-3">
      <input value={code} onChange={(e) => setCode(e.target.value)} autoFocus autoComplete="one-time-code"
        inputMode={recovery ? "text" : "numeric"} placeholder={recovery ? "XXXXXX-XXXXXX" : "123456"} data-testid="mfa-code-input"
        className="w-full rounded-xl border-2 border-[var(--brand-border)] px-4 py-3 text-center text-lg tracking-widest font-mono" />
      <button className="btn-primary w-full" disabled={busy || !code.trim()} data-testid="mfa-submit">{busy ? "Verificando…" : "Verificar"}</button>
      {setRecovery && <button type="button" className="text-xs underline text-[var(--brand-text-muted)]" onClick={() => setRecovery(!recovery)} data-testid="mfa-toggle-recovery">
        {recovery ? "Usar el código de mi app" : "Usar un código de recuperación"}</button>}
    </form>
  );
};

export default function MfaVerify() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { loginToken } = useAuth();
  const [enroll, setEnroll] = useState(null);
  const [recovery, setRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const [tokens, setTokens] = useState(null);
  const mfaToken = state?.mfa_token;

  useEffect(() => {
    if (mfaToken && !state.enrolled) {
      api.post("/auth/mfa/enroll/start", { mfa_token: mfaToken }).then((r) => setEnroll(r.data))
        .catch((e) => { toast.error(errMsg(e)); navigate("/ingresar", { replace: true }); });
    }
  }, [mfaToken]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mfaToken) return <Navigate to="/ingresar" replace />;

  const finish = async (data) => { await loginToken(data.access_token, data.refresh_token); navigate("/admin", { replace: true }); };
  const submit = async (code) => {
    setBusy(true);
    try {
      if (!state.enrolled) {
        const r = await api.post("/auth/mfa/enroll/confirm", { mfa_token: mfaToken, code });
        setTokens(r.data); setCodes(r.data.recovery_codes);
      } else {
        const r = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, ...(recovery ? { recovery_code: code } : { code }) });
        await finish(r.data);
      }
    } catch (e) {
      toast.error(errMsg(e));
      if ([401, 429].includes(e.response?.status) && /venció|Demasiados/.test(errMsg(e))) navigate("/ingresar", { replace: true });
    } finally { setBusy(false); }
  };

  return (
    <Section className="py-14 max-w-md">
      <div className="card-brand p-7 sm:p-8" data-testid="mfa-page">
        {codes ? <RecoveryCodes codes={codes} onContinue={() => finish(tokens)} /> : state.enrolled ? (
          <>
            <h1 className="h-section text-2xl mb-1">Verificación en dos pasos</h1>
            <p className="text-sm text-[var(--brand-text-muted)] mb-5">{recovery ? "Ingresá uno de tus códigos de recuperación." : "Ingresá el código de 6 dígitos de tu app de autenticación."}</p>
            <CodeForm onSubmit={submit} recovery={recovery} setRecovery={setRecovery} busy={busy} />
          </>
        ) : (
          <>
            <h1 className="h-section text-2xl mb-1">Activá la verificación en dos pasos</h1>
            <p className="text-sm text-[var(--brand-text-muted)] mb-4">Es obligatoria para super administradores. Escaneá el código con Google Authenticator, Microsoft Authenticator o similar, e ingresá el código que te muestra.</p>
            {enroll ? (
              <div className="flex flex-col items-center gap-2 mb-4">
                <img src={enroll.qr} alt="Código QR para tu app de autenticación" className="w-48 h-48" data-testid="mfa-qr" />
                <p className="text-xs text-[var(--brand-text-muted)]">¿No podés escanear? Clave: <span className="font-mono break-all" data-testid="mfa-secret">{enroll.secret}</span></p>
              </div>
            ) : <p className="text-sm mb-4">Generando código…</p>}
            <CodeForm onSubmit={submit} recovery={false} busy={busy || !enroll} />
          </>
        )}
      </div>
    </Section>
  );
}
